import { useState, useEffect, useRef } from 'react';
import { notificationAPI } from '@/lib/api';
import { Bell, Check, TrendingUp, Shield, Wallet, Info, X } from 'lucide-react';

const iconMap = {
  trade: TrendingUp,
  kyc: Shield,
  wallet: Wallet,
  system: Info,
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const { data } = await notificationAPI.getAll(20);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getTypeColor = (type, title) => {
    if (title?.toLowerCase().includes('rejected')) return 'text-red-400 bg-red-500/15';
    if (title?.toLowerCase().includes('approved') || title?.toLowerCase().includes('verified')) return 'text-emerald-400 bg-emerald-500/15';
    if (type === 'trade') return 'text-green-400 bg-green-500/15';
    if (type === 'kyc') return 'text-amber-400 bg-amber-500/15';
    return 'text-[var(--text-secondary)] bg-[var(--hover-bg)]';
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
        className="relative p-2 rounded-lg bg-[var(--hover-bg)] hover:bg-[var(--bg-surface)] border border-[var(--border-subtle)] transition-all duration-200"
      >
        <Bell className="w-4 h-4 text-[var(--text-secondary)]" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-[340px] max-h-[420px] bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg shadow-xl shadow-black/20 z-50 overflow-hidden" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
            <span className="text-sm font-semibold text-[var(--text-primary)] font-['Outfit']">Notifications</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} data-testid="mark-all-read-btn"
                  className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1 transition-colors">
                  <Check className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-0.5 rounded hover:bg-[var(--hover-bg)]">
                <X className="w-3.5 h-3.5 text-[var(--text-tertiary)]" />
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-[360px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No notifications yet</div>
            ) : (
              notifications.map((n) => {
                const Icon = iconMap[n.type] || Info;
                const colorClass = getTypeColor(n.type, n.title);
                return (
                  <div key={n.id} className={`px-4 py-3 border-b border-[var(--divide-color)] hover:bg-[var(--hover-bg)] transition-colors ${!n.read ? 'bg-green-500/5' : ''}`} data-testid={`notification-item-${n.id}`}>
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-[var(--text-primary)] leading-tight">{n.title}</p>
                          {!n.read && <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 mt-1" />}
                        </div>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
